import { useCallback, useEffect, useRef, useState } from 'react';
import userSettingsService from '../../../services/userSettings.service';

/** The single per-user setting key holding both sections' collapse state. */
const SETTING_KEY = 'quote_drawer_sections';

export type QuoteSection = 'accountDetails' | 'quoteDetails';

export interface QuoteSectionCollapseState {
  /** true = collapsed. */
  accountDetails: boolean;
  quoteDetails: boolean;
}

const DEFAULTS: QuoteSectionCollapseState = { accountDetails: false, quoteDetails: false };

/** Coerce an unknown stored/loaded value into a well-formed collapse state. */
function normalize(value: unknown): QuoteSectionCollapseState {
  const v = (value ?? {}) as Partial<Record<QuoteSection, unknown>>;
  return {
    accountDetails: Boolean(v.accountDetails),
    quoteDetails: Boolean(v.quoteDetails),
  };
}

function readCache(): QuoteSectionCollapseState | null {
  try {
    const raw = localStorage.getItem(userSettingsService.getCacheKey(SETTING_KEY));
    return raw ? normalize(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writeCache(state: QuoteSectionCollapseState): void {
  try {
    localStorage.setItem(userSettingsService.getCacheKey(SETTING_KEY), JSON.stringify(state));
  } catch {
    /* quota / unavailable — ignore; the server row stays the source of truth */
  }
}

/** isAuthenticated() reaches into MSAL (and its localStorage); treat any failure
 *  as "not authenticated" so the section state still works locally and never
 *  crashes the host page when MSAL storage is unavailable. */
function isAuthed(): boolean {
  try {
    return userSettingsService.isAuthenticated();
  } catch {
    return false;
  }
}

/**
 * Remembers the collapsed/expanded state of the Quote surfaces' "Account
 * Details" and "Quote Details" sections — per user, across reloads AND across
 * opening different quotes (the state is global, not keyed by quote). Used by
 * QuoteLaneEditorDrawer (both sections) and QuoteDetailPage (Quote Details).
 *
 * State is layered for a correct-yet-instant experience:
 *   1. synchronous localStorage read → correct first paint (no expand→collapse flash)
 *   2. server reconcile on mount → the user_settings row is the source of truth
 *   3. explicit toggles write through to localStorage + server, MERGED — so a
 *      surface that only shows one section (the standalone page) never clobbers
 *      the other section's saved value
 *
 * The server write is skipped while the initial load is in a FAILED state, so a
 * stale/default value can't overwrite real settings when the backend is briefly
 * unreachable (mirrors TanTable's load-failure protection). Writes fire only on
 * an explicit user toggle, never on load; and a slow load never overwrites a
 * toggle the user already made on this mount.
 */
export default function useQuoteSectionCollapse(): {
  collapsed: QuoteSectionCollapseState;
  setSection: (section: QuoteSection, collapsed: boolean) => void;
} {
  const [collapsed, setCollapsed] = useState<QuoteSectionCollapseState>(() => readCache() ?? DEFAULTS);
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;

  // Set true only on a real load error (a 404 → null is "no setting yet", not a
  // failure). Blocks the server write so we never push a stale value over a row
  // we couldn't read. The user toggle still updates state + the local cache.
  const loadFailedRef = useRef(false);
  // Once the user has toggled this mount, their live action is authoritative —
  // a late server response must not revert it.
  const userTouchedRef = useRef(false);

  useEffect(() => {
    if (!isAuthed()) return;
    let cancelled = false;
    const controller = new AbortController();
    userSettingsService
      .getUserSetting(SETTING_KEY, controller.signal)
      .then((setting) => {
        if (cancelled || userTouchedRef.current) return;
        // null => no saved setting (404); keep the cached/default state.
        if (setting && setting.setting_value != null) {
          const next = normalize(setting.setting_value);
          collapsedRef.current = next;
          setCollapsed(next);
          writeCache(next);
        }
      })
      .catch(() => {
        if (cancelled || controller.signal.aborted) return;
        loadFailedRef.current = true;
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const setSection = useCallback((section: QuoteSection, value: boolean) => {
    userTouchedRef.current = true;
    const next: QuoteSectionCollapseState = { ...collapsedRef.current, [section]: value };
    collapsedRef.current = next;
    setCollapsed(next);
    writeCache(next);
    // Best-effort server persist — only on an explicit toggle, and only when the
    // initial load didn't fail (else we'd risk clobbering an unread row).
    if (!loadFailedRef.current && isAuthed()) {
      userSettingsService.saveUserSetting(SETTING_KEY, next).catch(() => {
        /* best-effort — localStorage keeps the value for this browser */
      });
    }
  }, []);

  return { collapsed, setSection };
}
